import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { UserInfoService } from '../auth-service/user-info-service';
import { SystemFunctionType } from 'src/app/api/proxy/auth/authentication-models';
import { VitalScoreApiConfiguration } from 'src/app/api/proxy/vitalscore/vital-score-api-configuration';
import { WearablesApiConfiguration } from 'src/app/api/proxy/wearables/wearables-api-configuration';
import { FaceScanApiConfiguration } from 'src/app/api/proxy/facescan/face-scan-api-configuration';
import {
  dashboardOrganizationListGet,
  dashboardUserProfileOrganizationIdPost,
  dashboardHealthScoreOrganizationIdPost,
  dashboardTimeInAppOrganizationIdPost
} from 'src/app/api/proxy/vitalscore/functions';
import {
  dashboardDeviceSummaryOrganizationIdPost,
  dashboardPhysicalSummaryOrganizationIdPost,
  dashboardSleepSummaryOrganizationIdPost
} from 'src/app/api/proxy/wearables/functions';
import { dashboardOrganizationScanSummaryOrganizationIdPost } from 'src/app/api/proxy/facescan/functions';
import { firstValueFrom, timeout } from 'rxjs';
import { DashboardItemModel, DashboardSummaryType, OrganizationItemModel } from 'src/app/api/proxy/vitalscore/vitalscore-models';
import { PromptService } from '../promtp-service/prompt.service';
import { DashboardDeviceItemModel, DashboardPhysicalItemModel, DashboardSleepItemModel } from 'src/app/api/proxy/wearables/wearables-models';
import { DashboardHealthCheckItemModel } from 'src/app/api/proxy/facescan/facescan-models';
import { DashboardDateSelectionType } from './DashboardDateSelectionType';
import { TranslatorService } from '../translator-service/translator.service';
import { Network } from '@capacitor/network';

@Injectable({
  providedIn: 'root',
})
export class DashboardService {

  private NETWORK_TIMEOUT = 30000;
  private OrganizationList: OrganizationItemModel[] | null = null;

  constructor(
    private http: HttpClient,
    private vitalScoreCfg: VitalScoreApiConfiguration,
    private wearablesCfg: WearablesApiConfiguration,
    private faceScanCfg: FaceScanApiConfiguration,
    private _PromptService: PromptService,
    private _TranslatorService: TranslatorService
  ) {


  }

  public async userIsAllowed(): Promise<boolean> {
    return await UserInfoService.userIdAllowed(SystemFunctionType.VivascoreGroupDashboard);
  }


  calculateDaysBetweenDates(fromDate: Date, toDate: Date): number {
    const oneDay = 24 * 60 * 60 * 1000; // Milliseconds in one day
    const diffDays = Math.round(Math.abs((toDate.getTime() - fromDate.getTime()) / oneDay));
    return diffDays;
  }

  // Add the calculateMonthsBetweenDates method in your class
  calculateMonthsBetweenDates(fromDate: Date, toDate: Date): number {
    const fromYear = fromDate.getFullYear();
    const fromMonth = fromDate.getMonth();
    const toYear = toDate.getFullYear();
    const toMonth = toDate.getMonth();
    return (toYear - fromYear) * 12 + (toMonth - fromMonth);
  }

  public getSelectionOptions(): DashboardDateSelectionType[] {
    const options: DashboardDateSelectionType[] = [];

    // Get yesterday's date and set the time to 23:59:59
    const yesterDay = new Date();
    yesterDay.setDate(yesterDay.getDate() - 1);
    yesterDay.setHours(23, 59, 59, 999); // Set hours, minutes, seconds, and milliseconds

    // Populate with example data
    options.push(Object.assign(new DashboardDateSelectionType(), {
      SelectionName: this._TranslatorService.translateText('dashboard-service.date-selection-type.last-seven-days'),
      FromDate: new Date(new Date().setDate(yesterDay.getDate() - 7)),
      ToDate: yesterDay,
      SummaryType: DashboardSummaryType.Daily,
      HorizontalItems: 7
    }));

    options.push(Object.assign(new DashboardDateSelectionType(), {
      SelectionName: this._TranslatorService.translateText('dashboard-service.date-selection-type.month-to-date-daily'),
      FromDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      ToDate: yesterDay,
      SummaryType: DashboardSummaryType.Daily,
      HorizontalItems: this.calculateDaysBetweenDates(
        new Date(new Date().getFullYear(), new Date().getMonth(), 1),
        yesterDay
      )
    }));

    options.push(Object.assign(new DashboardDateSelectionType(), {
      SelectionName: this._TranslatorService.translateText('dashboard-service.date-selection-type.last-month-daily'),
      FromDate: new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1),
      ToDate: new Date(new Date().getFullYear(), new Date().getMonth(), 0),
      SummaryType: DashboardSummaryType.Daily,
      HorizontalItems: this.calculateDaysBetweenDates(
        new Date(new Date().getFullYear(), new Date().getMonth(), 1),
        yesterDay
      )
    }));

    options.push(Object.assign(new DashboardDateSelectionType(), {
      SelectionName: this._TranslatorService.translateText('dashboard-service.date-selection-type.last-month-weekly'),
      FromDate: new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1),
      ToDate: new Date(new Date().getFullYear(), new Date().getMonth(), 0),
      SummaryType: DashboardSummaryType.Weekly,
      HorizontalItems: 5
    }));

    options.push(Object.assign(new DashboardDateSelectionType(), {
      SelectionName: this._TranslatorService.translateText('dashboard-service.date-selection-type.last-twelve-weeks-weekly'),
      FromDate: new Date(new Date().setDate(yesterDay.getDate() - (7 * 12))),
      ToDate: yesterDay,
      SummaryType: DashboardSummaryType.Weekly,
      HorizontalItems: 12
    }));

    options.push(Object.assign(new DashboardDateSelectionType(), {
      SelectionName: this._TranslatorService.translateText('dashboard-service.date-selection-type.this-year-monthly'),
      FromDate: new Date(new Date().getFullYear(), 0, 1),
      ToDate: new Date(),
      SummaryType: DashboardSummaryType.Monthly,
      HorizontalItems: this.calculateMonthsBetweenDates(
        new Date(new Date().getFullYear(), 0, 1),
        new Date()
      )
    }));


    return options;
  }

  public async organizationList(): Promise<OrganizationItemModel[] | null> {

    if (this.OrganizationList) {
      return this.OrganizationList;
    }
    try {
      const obs = dashboardOrganizationListGet(this.http, this.vitalScoreCfg.rootUrl);
      const httpResponse = await firstValueFrom(obs.pipe(timeout(this.NETWORK_TIMEOUT)));
      const response = httpResponse.body!;


      if (response != null && response.length > 0) {
        this.OrganizationList = response;
      }
      return this.OrganizationList;

    } catch (error) {
      await this._PromptService.showNetworkConnectionError(error, 'organizationList');
      return null;
    }
  }
  //Vivascore

  public async userProfiles(organizationId: string, dateSelection: DashboardDateSelectionType): Promise<DashboardItemModel[] | null> {
    const status = await Network.getStatus();
    if (!status.connected) {
      return null;
    }

    try {
      const obs = dashboardUserProfileOrganizationIdPost(this.http, this.vitalScoreCfg.rootUrl, {
        organizationId: organizationId,
        body: {
          startDate: dateSelection.FromDate.toISOString(),
          endDate: dateSelection.ToDate.toISOString(),
          summaryType: dateSelection.SummaryType
        }
      });
      const httpResponse = await firstValueFrom(obs.pipe(timeout(this.NETWORK_TIMEOUT)));
      const response = httpResponse.body!;
      return response;
    } catch (error) {
      await this._PromptService.showNetworkConnectionError(error, 'userProfiles');
      return null;
    }
  }

  public async healthScoreAsync(organizationId: string, dateSelection: DashboardDateSelectionType): Promise<DashboardItemModel[] | null> {
    const status = await Network.getStatus();
    if (!status.connected) {
      return null;
    }

    try {
      const obs = dashboardHealthScoreOrganizationIdPost(this.http, this.vitalScoreCfg.rootUrl, {
        organizationId: organizationId,
        body: {
          startDate: dateSelection.FromDate.toISOString(),
          endDate: dateSelection.ToDate.toISOString(),
          summaryType: dateSelection.SummaryType
        }
      });
      const httpResponse = await firstValueFrom(obs.pipe(timeout(this.NETWORK_TIMEOUT)));
      const response = httpResponse.body!;
      return response;
    } catch (error) {
      await this._PromptService.showNetworkConnectionError(error, 'healthScoreAsync');
      return null;
    }
  }

  public async timeInAppAsync(organizationId: string, dateSelection: DashboardDateSelectionType): Promise<DashboardItemModel[] | null> {
    const status = await Network.getStatus();
    if (!status.connected) {
      return null;
    }

    try {
      const obs = dashboardTimeInAppOrganizationIdPost(this.http, this.vitalScoreCfg.rootUrl, {
        organizationId: organizationId,
        body: {
          startDate: dateSelection.FromDate.toISOString(),
          endDate: dateSelection.ToDate.toISOString(),
          summaryType: dateSelection.SummaryType
        }
      });
      const httpResponse = await firstValueFrom(obs.pipe(timeout(this.NETWORK_TIMEOUT)));
      const response = httpResponse.body!;
      return response;
    } catch (error) {
      await this._PromptService.showNetworkConnectionError(error, 'timeInAppAsync');
      return null;
    }
  }

  //Wearables
  public async deviceSummaryAsync(organizationId: string, dateSelection: DashboardDateSelectionType): Promise<DashboardDeviceItemModel[] | null> {
    const status = await Network.getStatus();
    if (!status.connected) {
      return null;
    }

    try {
      const obs = dashboardDeviceSummaryOrganizationIdPost(this.http, this.wearablesCfg.rootUrl, {
        organizationId: organizationId,
        body: {
          startDate: dateSelection.FromDate.toISOString(),
          endDate: dateSelection.ToDate.toISOString(),
          summaryType: dateSelection.SummaryType
        }
      });
      const httpResponse = await firstValueFrom(obs.pipe(timeout(this.NETWORK_TIMEOUT)));
      const response = httpResponse.body!;
      return response;
    } catch (error) {
      await this._PromptService.showNetworkConnectionError(error, 'deviceSummaryAsync');
      return null;
    }
  }

  public async physicalSummaryAsync(organizationId: string, dateSelection: DashboardDateSelectionType): Promise<DashboardPhysicalItemModel[] | null> {
    const status = await Network.getStatus();
    if (!status.connected) {
      return null;
    }

    try {
      const obs = dashboardPhysicalSummaryOrganizationIdPost(this.http, this.wearablesCfg.rootUrl, {
        organizationId: organizationId,
        body: {
          startDate: dateSelection.FromDate.toISOString(),
          endDate: dateSelection.ToDate.toISOString(),
          summaryType: dateSelection.SummaryType
        }
      });
      const httpResponse = await firstValueFrom(obs.pipe(timeout(this.NETWORK_TIMEOUT)));
      const response = httpResponse.body!;
      return response;
    } catch (error) {
      await this._PromptService.showNetworkConnectionError(error, 'physicalSummaryAsync');
      return null;
    }
  }

  public async sleepSummaryAsync(organizationId: string, dateSelection: DashboardDateSelectionType): Promise<DashboardSleepItemModel[] | null> {
    const status = await Network.getStatus();
    if (!status.connected) {
      return null;
    }

    try {
      const obs = dashboardSleepSummaryOrganizationIdPost(this.http, this.wearablesCfg.rootUrl, {
        organizationId: organizationId,
        body: {
          startDate: dateSelection.FromDate.toISOString(),
          endDate: dateSelection.ToDate.toISOString(),
          summaryType: dateSelection.SummaryType
        }
      });
      const httpResponse = await firstValueFrom(obs.pipe(timeout(this.NETWORK_TIMEOUT)));
      const response = httpResponse.body!;
      return response;
    } catch (error) {
      await this._PromptService.showNetworkConnectionError(error, 'scanSummaryAsync');
      return null;
    }
  }

  //healthCheck
  public async scanSummaryAsync(organizationId: string, dateSelection: DashboardDateSelectionType): Promise<DashboardHealthCheckItemModel[] | null> {
    const status = await Network.getStatus();
    if (!status.connected) {
      return null;
    }

    try {
      const obs = dashboardOrganizationScanSummaryOrganizationIdPost(this.http, this.faceScanCfg.rootUrl, {
        organizationId: organizationId,
        body: {
          startDate: dateSelection.FromDate.toISOString(),
          endDate: dateSelection.ToDate.toISOString(),
          summaryType: dateSelection.SummaryType
        }
      });
      const httpResponse = await firstValueFrom(obs.pipe(timeout(this.NETWORK_TIMEOUT)));
      const response = httpResponse.body!;
      return response;
    } catch (error) {
      await this._PromptService.showNetworkConnectionError(error, 'scanSummaryAsync');
      return null;
    }
  }


}

